// Rules for VALIDATION_CONSTRAINTS.md section 2 — sequence flows (edges).
//
// Edges carry semantics for the engine via two channels:
//   1. edge.data.isRecursive: annotation flag; engine stores these in
//      RecursiveConnections as overlays on existing non-recursive edges.
//   2. edge.sourceHandle + sourceNode.data.conditions: gateway branches.
//      A condition entry with empty expression marks that outgoing branch as
//      the gateway default path (IsDefault=true at export time).
//
// All rules here fire at process scope on the edge list.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getNode,
  isEndEvent,
  isStartEvent,
  isBoundaryEvent,
  isAssociationEdge,
  labelOf,
} from '../helpers';

// 2.1 — From/To of every edge reference existing nodes.
const rule_2_1_EdgeEndpointsExist: ValidationRule = {
  id: '2.1',
  description: 'Every edge From/To must reference an existing node in the process',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      const src = getNode(lookup, e.source);
      const tgt = getNode(lookup, e.target);
      if (!src) {
        issues.push({
          severity: 'error',
          rule: '2.1',
          message: `Edge ${e.id} references missing source node "${e.source}".`,
          edgeIds: [e.id],
        });
      }
      if (!tgt) {
        issues.push({
          severity: 'error',
          rule: '2.1',
          message: `Edge ${e.id} references missing target node "${e.target}".`,
          edgeIds: [e.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_1_EdgeEndpointsExist);

// 2.2 — No duplicate (From, To) pair in process.edges. Report second and later.
const rule_2_2_NoDuplicateFlow: ValidationRule = {
  id: '2.2',
  description: 'No duplicate (From, To) pair in process edges',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    const seen = new Map<string, string>(); // key -> first edge id
    for (const e of process.edges) {
      const key = `${e.source}->${e.target}`;
      const first = seen.get(key);
      if (first !== undefined) {
        issues.push({
          severity: 'error',
          rule: '2.2',
          message: `Duplicate sequence flow ${e.source} -> ${e.target} (first defined as ${first}).`,
          edgeIds: [e.id],
        });
      } else {
        seen.set(key, e.id);
      }
    }
    return issues;
  },
};
registerRule(rule_2_2_NoDuplicateFlow);

// 2.3 — Edge source must not be an EndEvent.
const rule_2_3_SourceNotEndEvent: ValidationRule = {
  id: '2.3',
  description: 'Edge source must not be an EndEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      const src = getNode(lookup, e.source);
      if (src && isEndEvent(src)) {
        issues.push({
          severity: 'error',
          rule: '2.3',
          message: `${labelOf(src)} is an EndEvent and cannot have outgoing flow (edge ${e.id}).`,
          edgeIds: [e.id],
          nodeIds: [src.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_3_SourceNotEndEvent);

// 2.4 — Edge target must not be a StartEvent or BoundaryEvent.
// BoundaryEvent attachment is via parentNode, not sequence flow.
const rule_2_4_TargetNotStartOrBoundary: ValidationRule = {
  id: '2.4',
  description: 'Edge target must not be a StartEvent or BoundaryEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      if (isAssociationEdge(e)) continue; // visual-only boundary link
      const tgt = getNode(lookup, e.target);
      if (!tgt) continue;
      if (isStartEvent(tgt)) {
        issues.push({
          severity: 'error',
          rule: '2.4',
          message: `${labelOf(tgt)} is a StartEvent and cannot have incoming flow (edge ${e.id}).`,
          edgeIds: [e.id],
          nodeIds: [tgt.id],
        });
      } else if (isBoundaryEvent(tgt)) {
        issues.push({
          severity: 'error',
          rule: '2.4',
          message: `${labelOf(tgt)} is a BoundaryEvent — attach via parentNode, not sequence flow (edge ${e.id}).`,
          edgeIds: [e.id],
          nodeIds: [tgt.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_4_TargetNotStartOrBoundary);

// 2.5 — No self-loop on a normal connection (source===target).
// Recursive-flagged self-edges are still invalid: engine requires the recursive
// annotation to overlay a real non-recursive (From,To) edge to traverse, and a
// real self-edge is not a valid non-recursive connection either.
const rule_2_5_NoSelfLoop: ValidationRule = {
  id: '2.5',
  description: 'Edges must not self-loop (source === target)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      if (e.source === e.target) {
        const n = getNode(lookup, e.source);
        issues.push({
          severity: 'error',
          rule: '2.5',
          message: `${n ? labelOf(n) : e.source} has a self-loop edge ${e.id}; cycles must flow through another node and be annotated with RecursiveConnections.`,
          edgeIds: [e.id],
          nodeIds: n ? [n.id] : undefined,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_5_NoSelfLoop);

// 2.6 — Previously required a separate non-recursive twin for every
// recursive edge. The loader now collapses RecursiveConnections onto the
// matching Connection as a single edge carrying `data.isRecursive = true`,
// so the twin-edge check no longer matches the editor's edge model.
// Orphan RecursiveConnections (no matching Connection) are surfaced by
// the loader as lone recursive edges and remain flagged by rule 13.6.

/**
 * Returns true if the edge is "default-flagged" at the source gateway:
 * the source node's conditions contain an entry whose String(path) matches
 * edge.sourceHandle and whose expression is empty/whitespace. Matches the
 * export-time logic in utils/pssHelper.ts (IsDefault derivation).
 */
const isEdgeDefaultAtSource = (source: any, edgeSourceHandle: string | null | undefined): boolean => {
  if (!source || edgeSourceHandle == null) return false;
  const conds: Array<{ path: number; expression: string }> = source.data?.conditions ?? [];
  const match = conds.find(c => String(c.path) === edgeSourceHandle);
  if (!match) return false;
  return !match.expression || match.expression.trim() === '';
};

// 2.7 — IsDefault=true edges only from XorGateway or InclusiveGateway.
const rule_2_7_DefaultOnlyFromXorOrInclusive: ValidationRule = {
  id: '2.7',
  description: 'Edges with IsDefault=true must originate from an XorGateway or InclusiveGateway',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      const src = getNode(lookup, e.source);
      if (!src) continue;
      if (!isEdgeDefaultAtSource(src, e.sourceHandle)) continue;
      if (src.type !== 'XorGateway' && src.type !== 'InclusiveGateway') {
        issues.push({
          severity: 'error',
          rule: '2.7',
          message: `Edge ${e.id} is marked as default (empty expression) but source ${labelOf(src)} is not an XorGateway or InclusiveGateway.`,
          edgeIds: [e.id],
          nodeIds: [src.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_7_DefaultOnlyFromXorOrInclusive);

// 2.8 — For Xor/Inclusive gateways with a default condition, exactly one
// outgoing edge must carry sourceHandle === String(cond.path), and its target
// must equal the computed DefaultPath target for the gateway. Because the
// DefaultPath is *derived* from the default-flagged edge's target at export,
// we simply require: there is exactly one default-flagged outgoing edge per
// default condition, and it has a target. 2.9 enforces the at-most-one part
// across the whole gateway; 2.8 enforces the resolution of each default cond.
const rule_2_8_DefaultPathResolvesToEdge: ValidationRule = {
  id: '2.8',
  description: 'Each default condition on a gateway must be matched by exactly one outgoing edge whose sourceHandle equals String(cond.path)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway' && n.type !== 'InclusiveGateway') continue;
      const conds: Array<{ path: number; expression: string }> = (n.data as any)?.conditions ?? [];
      const defaultConds = conds.filter(c => !c.expression || c.expression.trim() === '');
      if (defaultConds.length === 0) continue;
      const outgoing = (lookup.outgoingByNode.get(n.id) ?? []);
      for (const cond of defaultConds) {
        const handle = String(cond.path);
        const matching = outgoing.filter(e => e.sourceHandle === handle);
        if (matching.length === 0) {
          issues.push({
            severity: 'error',
            rule: '2.8',
            message: `${labelOf(n)} default condition (path ${cond.path}) has no matching outgoing edge (sourceHandle="${handle}").`,
            nodeIds: [n.id],
          });
        } else if (matching.length > 1) {
          issues.push({
            severity: 'error',
            rule: '2.8',
            message: `${labelOf(n)} default condition (path ${cond.path}) matches ${matching.length} outgoing edges; expected exactly one.`,
            nodeIds: [n.id],
            edgeIds: matching.map(e => e.id),
          });
        } else {
          const edge = matching[0];
          if (!edge.target) {
            issues.push({
              severity: 'error',
              rule: '2.8',
              message: `${labelOf(n)} default edge ${edge.id} (path ${cond.path}) has no target; DefaultPath would be unresolvable.`,
              nodeIds: [n.id],
              edgeIds: [edge.id],
            });
          }
        }
      }
    }
    return issues;
  },
};
registerRule(rule_2_8_DefaultPathResolvesToEdge);

// 2.9 — At most one default edge per gateway.
// Distinct default-flagged outgoing edges, identified via
// isEdgeDefaultAtSource, which counts any outgoing edge whose
// (handle -> condition) is empty-expression.
const rule_2_9_AtMostOneDefaultPerGateway: ValidationRule = {
  id: '2.9',
  description: 'At most one default (IsDefault=true) outgoing edge per gateway',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway' && n.type !== 'InclusiveGateway') continue;
      const outgoing = lookup.outgoingByNode.get(n.id) ?? [];
      const defaultEdges = outgoing.filter(e => isEdgeDefaultAtSource(n, e.sourceHandle));
      if (defaultEdges.length > 1) {
        issues.push({
          severity: 'error',
          rule: '2.9',
          message: `${labelOf(n)} has ${defaultEdges.length} default outgoing edges; at most one is allowed.`,
          nodeIds: [n.id],
          edgeIds: defaultEdges.map(e => e.id),
        });
      }
    }
    return issues;
  },
};
registerRule(rule_2_9_AtMostOneDefaultPerGateway);
